/* eslint-disable prefer-const */
import type { PropsWithChildren } from 'react';
import React from 'react';
import { FormattedMessage, defineMessages, type IntlShape } from 'react-intl-next';

import type { CreateUIAnalyticsEvent } from '@atlaskit/analytics-next/types';
import {
	DefaultExtensionProvider,
	type ExtensionComponentProps,
	type ExtensionDeprecationStatus,
	type ExtensionHandler,
	type ExtensionHandlers,
	type ExtensionManifest,
	type ExtensionProvider,
	type MaybeESModule,
	type PreloadableExtensionModuleNode,
} from '@atlaskit/editor-common/extensions';
import type { ProviderFactory } from '@atlaskit/editor-common/provider-factory';
import type { PublicPluginAPI } from '@atlaskit/editor-common/types';
import type { ADNode } from '@atlaskit/editor-common/validator';
import type { EditorActions } from '@atlaskit/editor-core';
import type { ExtensionPlugin } from '@atlaskit/editor-plugins/extension';
import Link from '@atlaskit/link';
import { Text } from '@atlaskit/primitives/compiled';
import { createManifest as createNativeEmbedManifest } from '@atlassian/native-embeds-editor-extension/manifest';

import { getAIPanelsProvider } from '@atlassian/editor-plugin-ai/AIPanels';
import type { ExtensionHandlerWithReferenceFn } from '@atlassian/editor-referentiality';

import { createChartsExtension } from '@confluence/charts/entry-points/createChartsExtension.ts';
import { createDatabaseExtension } from '@confluence/confluence-databases-extension';
import { isContentTypeEnabledInCurrentEnv } from '@confluence/content-types-utils/entry-points/isContentTypeEnabledInCurrentEnv.ts';
import { createPremiumExtensionManifests } from '@confluence/custom-sites-extensions/entry-points/createPremiumExtensionManifests.ts';
import type { ExperienceTrackerAPI } from '@confluence/experience-tracker/entry-points/ExperienceTracker.ts';
import { expVal } from '@confluence/feature-experiments';
import { fg } from '@confluence/feature-gating';
import type { FlagsStateContainer } from '@confluence/flags/entry-points/FlagsStateContainer.ts';
import { getMacroId } from '@confluence/macro-tracker/entry-points/getExtensionKey.ts';
import { createWhiteboardExtension } from '@confluence/whiteboard-extension/entry-points/createWhiteboardExtension.ts';
import { createTourableRedactionExtension } from '@confluence/redactions/entry-points/extension.ts';
import { createCreateWithRovoExtension } from '@confluence/experiment-create-with-rovo-quick-insert/entry-points/createCreateWithRovoExtension.ts';
import type { OpenModalParams } from '@confluence/smart-creation-modal-store/entry-points/smartCreationModalStore.tsx';
import type { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';

import { shouldIncludeMacroInQuickInsert } from '../../extensions-common/macroKeyResolver.ts';
import { getAutoConverter } from '../../editor-extensions/index.ts';
import { isAllowedWithFeatureFlags } from '../../editor-extensions/blocklist.ts';
import { getDefaultParamValue, unwrapMacroParams } from '../../editor-extensions/transformers.ts';
import { EXTENSION_TYPE } from '../../extensionConstants.ts';
import {
	DEPRECATED_MACROS,
	MACRO_DEPRECATION_INFO_URL,
	isMacroDeprecated,
} from '../../extensions-common/macroDeprecation.ts';
import {
	getMacroDescriptionOverride,
	getMacroTitleOverride,
} from '../../extensions-common/macroOverrides.ts';
import { isBodiedExtension } from '../../extensions-common/extensionNodeTypes.ts';
import { getRedactedMacroPlaceholderExtensionManifest } from '../../renderer-extensions/redacted-macro-placeholder/manifest.ts';

import {
	getMissingConfigPlaceholderComponent,
	getNonRenderablePlaceholderComponent,
} from './MacroComponents.tsx';
import { nestedTableExtension } from './custom-extensions/nestedTable.ts';
import type {
	ConfluencePageContext,
	LegacyMacroManifest,
	OnCompleteCallbackFnType,
	RendererExtensionHandlers,
} from './extensionTypes.ts';
import type { MacroConfig } from './extension-provider-types.ts';
import { transformFormDetailsIntoFields } from './field-helpers.ts';
import { remapCustomFields, transformParams } from './field-mappings/index.ts';
import {
	buildIconObject,
	canRenderInTheEditor,
	getExtensionBodyType,
	getVisibleParameters,
	prepareMacro,
	safeGetMacroName,
	shouldShowPlaceholder,
	shouldUseMacroBrowser,
} from './manifest-helpers.tsx';
import {
	createQuickInsertModule,
	filterOutHiddenMacros,
	getSelectedBodiedExtensionNode,
} from './utils.ts';
// resolvers
import { getAttachmentResolverFor } from './data-resolvers/attachment-resolver.ts';
import { calendarResolver } from './data-resolvers/calendar-resolver.ts';
import { confluenceContentResolver } from './data-resolvers/confluence-content-resolver.ts';
import { customPrefixResolver } from './data-resolvers/custom-prefix-resolver.ts';
import { customValueResolver } from './data-resolvers/custom-value-resolver.ts';
import { getExtensionManifest } from './data-resolvers/getExtensionManifest.ts';
import { labelResolver } from './data-resolvers/label-resolver.ts';
import { spaceAndPageResolver } from './data-resolvers/space-and-page-resolver.ts';
import { getSpaceKeyAndPageTitleContentResolver } from './data-resolvers/space-key-page-title-content-resolver.ts';
import {
	buildResolver as buildSpacekeyResolver,
	spacekeyResolver,
} from './data-resolvers/spacekey-resolver.ts';
import { getTemplateResolver } from './data-resolvers/template-resolver.tsx';
import { unsupportedValueResolver } from './data-resolvers/unsupported-value-resolver.ts';
import { usernameResolver } from './data-resolvers/username-resolver.ts';
import { widthPercentageResolver } from './data-resolvers/width-percentage-resolver.ts';
// transformers
import { cqlDeserializer, cqlSerializer } from './cql/transformers.ts';

const i18n = defineMessages({
	deprecatedMacroDescription: {
		id: 'fabric-extension-lib.deprecation.description',
		defaultMessage:
			'This macro has been deprecated. <alink>Find out more about this change</alink>',
		description:
			'Deprecation note that appears under the macro description in the configuration panel for certain macros that will be removed from Confluence. <alink> goes to this page: https://support.atlassian.com/confluence-cloud/docs/learn-which-macros-are-being-removed/. Please ensure the translation matches that of "macro.browser.deprecation.desc" from CONFCLOUD/confluence repository (Confluence Monolith) as they are the same.',
	},
	configValuePrefixRequired: {
		id: 'fabric-extension-lib.config-value-prefix.required',
		defaultMessage: '{fieldValue} (required)',
		description:
			'Used to show that a label, space, or other field value is required in order to show up in macro results.  This will appear in a list of field values that are being used to search for blogs or other content to display in a macro.',
	},
	configValuePrefixExcluded: {
		id: 'fabric-extension-lib.config-value-prefix.excluded',
		defaultMessage: '{fieldValue} (excluded)',
		description:
			'Used to show that a label, space, or other field value is excluded from showing up in macro results.  This will appear in a list of field values that are being used to search for blogs or other content to display in a macro.',
	},
});

const PLUS_MINUS_PREFIX_SETTINGS = (intl) => [
	{
		prefix: '+',
		labelFunc: (labelExcludingPrefix) =>
			intl.formatMessage(i18n.configValuePrefixRequired, {
				fieldValue: labelExcludingPrefix,
			}),
	},
	{
		prefix: '-',
		labelFunc: (labelExcludingPrefix) =>
			intl.formatMessage(i18n.configValuePrefixExcluded, {
				fieldValue: labelExcludingPrefix,
			}),
	},
];

// We downloaded the extension manifest
// earlier within query-preloaders
// the resulting manifest is cached
const getMacroLegacyManifest = getExtensionManifest;

// Exported for testing only
export const getMacroDeprecation = (
	macro: LegacyMacroManifest,
): ExtensionDeprecationStatus | undefined => {
	// TODO: remove this code when cleaning up deprecating_low_usage_macros FG after macro removal on Sep 25 2025
	if (DEPRECATED_MACROS.includes(macro.macroName)) {
		return {
			isDeprecated: true,
			message: (
				<Text weight="medium">
					⚠️&nbsp;
					<FormattedMessage
						{...i18n.deprecatedMacroDescription}
						values={{
							alink: (...chunks) => (
								<Link href={MACRO_DEPRECATION_INFO_URL} target="_blank">
									{chunks}
								</Link>
							),
						}}
					/>
				</Text>
			),
		};
	}
	return undefined;
};

const isFeatureEnabled = (feature: 'database' | 'whiteboard') => {
	if (fg('confluence_oasis_diasble_db_whiteboard_macros')) {
		return isContentTypeEnabledInCurrentEnv(feature);
	}
	return true;
};

const mapLegacyManifestsToModernManifest = async (
	legacyMacros: LegacyMacroManifest[],
	pageContext: ConfluencePageContext,
	extensionHandlers: RendererExtensionHandlers,
	getMigrationExtensionManifest: Function,
	openMacroBrowserToEditExistingMacro: Function,
	openMacroBrowserForInitialConfiguration: Function,
	editorAPI: PublicPluginAPI<[ExtensionPlugin]> | undefined,
	intl: IntlShape,
	macroBrowserConfig?: MacroConfig,
	editorActions?: EditorActions,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	onCompleteCallback?: OnCompleteCallbackFnType,
	isLivePage?: boolean,
	flags?: FlagsStateContainer,
	allowLegacyMacros = false,
) => {
	const isAllowed = isAllowedWithFeatureFlags();

	return legacyMacros
		.map((macro: LegacyMacroManifest) => {
			const manifest = transformLegacyMacrosToExtensionManifest(
				EXTENSION_TYPE.MACRO_CORE,
				macro,
				pageContext,
				extensionHandlers,
				!isAllowed(macro),
				openMacroBrowserToEditExistingMacro,
				openMacroBrowserForInitialConfiguration,
				editorAPI,
				intl,
				macroBrowserConfig,
				editorActions,
				createAnalyticsEvent,
				onCompleteCallback,
				isLivePage,
				allowLegacyMacros,
			);

			return manifest;
		})
		.concat(
			getMigrationExtensionManifest(
				pageContext.contentId,
				!!editorActions,
				intl,
				flags,
				createAnalyticsEvent,
			),
		);
};

// Export for testing ONLY
export const transformLegacyMacrosToExtensionManifest = (
	extensionType: string,
	macro: LegacyMacroManifest,
	pageContext: ConfluencePageContext,
	extensionHandlers: RendererExtensionHandlers,
	readonly: boolean,
	openMacroBrowserToEditExistingMacro: any,
	openMacroBrowserForInitialConfiguration: any,
	editorAPI: PublicPluginAPI<[ExtensionPlugin]> | undefined,
	intl: IntlShape,
	macroBrowserConfig?: MacroConfig,
	editorActions?: EditorActions,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	onCompleteCallback?: OnCompleteCallbackFnType,
	isLivePage?: boolean,
	allowLegacyMacros = false,
): ExtensionManifest => {
	const extensionKey = macro.macroName;
	let renderCache = null as MaybeESModule<any> | null;
	const isEditor = !!editorActions;
	const { enabled: isConfigPanelEnabled, optedOut: excludedMacros } =
		pageContext.features.configPanel || {};

	const adfNodeType = getExtensionBodyType(macro);

	// Override title & description in manifest for certain macros in Fabric editor only
	const macroOverrides = {
		title: getMacroTitleOverride(extensionKey, intl) ?? macro.title,
		description: getMacroDescriptionOverride(extensionKey, intl) ?? macro.description,
	};

	macro = { ...macro, ...macroOverrides };

	const defaultNode: PreloadableExtensionModuleNode = {
		type: adfNodeType,

		render: () => {
			const extensionHandler = extensionHandlers[extensionType];

			return Promise.resolve((extension: PropsWithChildren<ExtensionComponentProps<any>>) => {
				// per backend restrictions, spaceKey and templateName are required in the extension's macroParams object in order for create-from-template macro to work for autogenerated blueprint index pages.
				if (extension.node.extensionKey === 'create-from-template') {
					const macroParams = extension.node.parameters?.['macroParams'];

					if (!macroParams?.spaceKey) {
						macroParams.spaceKey = { value: pageContext.spaceKey };
					}
					if (!macroParams?.templateName) {
						macroParams.templateName = macroParams.contentBlueprintId;
					}
				}

				// if an override is provided for title, update the title for extension label lozenge component
				// This is necessary to update existing macros on a page after a title change rolls out
				if (isEditor && extension.node.parameters.macroMetadata && macroOverrides?.title) {
					extension.node.parameters.macroMetadata.title = macroOverrides.title;
				}

				const showPlaceholder = shouldShowPlaceholder(
					macro,
					excludedMacros,
					extension.node,
					isEditor,
				);

				if ((isConfigPanelEnabled || !isEditor) && showPlaceholder) {
					return getMissingConfigPlaceholderComponent(macro, extension.node, isEditor);
				}

				if (isConfigPanelEnabled && isEditor && !canRenderInTheEditor(macro)) {
					return getNonRenderablePlaceholderComponent(
						macro,
						getExtensionBodyType(macro),
						isLivePage,
					);
				}

				if (typeof extensionHandler === 'function') {
					return (extensionHandler as ExtensionHandler<any>)(extension.node, {
						references: extension.references,
						actions: extension.actions,
					});
				}
				return extensionHandler.render(extension.node, {
					references: extension.references,
					actions: extension.actions,
				});
			});
		},

		renderSync() {
			return renderCache;
		},

		async preloadRender() {
			// Extension provider's preload function will loop through all nodes and call this function
			// See platform/packages/editor/editor-common/src/extensions/default-extension-provider.ts
			renderCache = await this.render();
		},
	};

	const allowEdit = allowLegacyMacros || !readonly;

	if (
		allowEdit &&
		(getVisibleParameters(macro).length > 0 || shouldUseMacroBrowser(macro, excludedMacros))
	) {
		const editInMacroBrowser = async (data, actions) => {
			let selectedNode: ADNode | undefined;
			/**
			 * In API v1, we used to pass the whole `node` as `parameters`.
			 * We fixed it in API v2 and now only passing `parameters`.
			 * But Bodied Extensions inside Macro Browser still need the body for preview.
			 * @see DEVHELP-6152
			 */
			if (isBodiedExtension(adfNodeType)) {
				// Currently, there's no other way to get selected node without using private API.
				selectedNode = getSelectedBodiedExtensionNode(editorActions);
			}

			const currentNode = await prepareMacro(
				{
					name: macro.macroName,
					params: unwrapMacroParams(data.macroParams),
				},
				pageContext.contentId,
				selectedNode,
				intl,
			);

			const defaultParameterValue = getDefaultParamValue(data.macroParams || {});
			if (defaultParameterValue) {
				const macroParams = currentNode.attrs?.parameters?.macroParams;
				if (macroParams) {
					macroParams[''] = defaultParameterValue;
				}
			}

			const macroFilter = undefined;
			const extensionAnalyticsInfo = {
				createAnalyticsEvent,
				contentId: pageContext.contentId,
			};

			const updateMacro = (newNode) => {
				selectedNode = getSelectedBodiedExtensionNode(editorActions);
				actions.doc.update(selectedNode?.attrs?.localId, () => ({
					attrs: newNode.attrs,
					marks: newNode.marks,
					content: newNode.content,
				}));
			};

			const newNode = await openMacroBrowserToEditExistingMacro(
				currentNode,
				macroBrowserConfig,
				macroFilter,
				extensionAnalyticsInfo,
				updateMacro,
				intl,
			);

			// For bodiedExtension we should update the content
			if (isBodiedExtension(adfNodeType) && actions?.doc?.update) {
				actions.doc.update(selectedNode?.attrs?.localId, () => ({
					attrs: newNode.attrs,
					marks: newNode.marks,
					content: newNode.content,
				}));
				return;
			}

			return newNode.attrs.parameters || {};
		};

		let params;

		if (!isMacroDeprecated(extensionKey)) {
			defaultNode.update = async (data, actions) => {
				// The adf parameters for macros look like:
				// {
				//    macroParams: {
				//        q: {value: 1},
				//        search: {value: 'my keyword'}
				//    },
				//    macroMetadata: {}
				// }

				// The only editable bit is what is inside the `macroParams`. This method unwraps the params and pass it to
				// the context panel for editing, and then wraps it back after saving.
				const transformBefore = (parameters) => {
					params = unwrapMacroParams(parameters.macroParams);
					return transformParams(
						'transformBefore',
						macro,
						params,
						pageContext,
						createAnalyticsEvent,
					);
				};

				const transformAfter = async (initialParameters) => {
					const parameters = transformParams(
						'transformAfter',
						macro,
						initialParameters,
						pageContext,
						createAnalyticsEvent,
					);

					// macros might have metadata changed based on macroParams so we need to fetch again. The rule changes on
					// each of them so we can't do it client side.
					const macroDefinition: any = {
						name: macro.macroName,
						params: parameters,
					};
					const defaultParameterValue = getDefaultParamValue(parameters);
					if (defaultParameterValue) {
						macroDefinition.defaultParameterValue = defaultParameterValue;
					}
					const node = await prepareMacro(macroDefinition, pageContext.contentId, null, intl);

					// add a new macroId if we don't already have one
					if (node.attrs.parameters) {
						if (!node.attrs.parameters.macroMetadata) {
							node.attrs.parameters.macroMetadata = {};
						}

						if (!node.attrs.parameters.macroMetadata['macroId']) {
							node.attrs.parameters.macroMetadata['macroId'] = {
								value: getMacroId(node.attrs.parameters),
							};
						}
					}

					return node.attrs.parameters || {};
				};

				if (!isConfigPanelEnabled || shouldUseMacroBrowser(macro, excludedMacros)) {
					return editInMacroBrowser(data, actions);
				}

				return actions!.editInContextPanel(transformBefore, transformAfter);
			};
		}

		if (getVisibleParameters(macro).length > 0) {
			defaultNode.getFieldsDefinition = () => {
				const fields = macro.formDetails.parameters.map((params) =>
					transformFormDetailsIntoFields(params, {
						pluginKey: macro.pluginKey,
						macroName: safeGetMacroName(macro),
					}),
				);

				return remapCustomFields(
					macro,
					fields,
					pageContext,
					intl,
					params,
					createAnalyticsEvent,
					isLivePage,
				);
			};
		}
	}

	return {
		type: extensionType,
		key: extensionKey,
		title: macro.title,
		description: macro.description,
		deprecation: getMacroDeprecation(macro),
		icons: buildIconObject(macro),
		documentationUrl: macro.formDetails.documentationUrl || undefined,
		modules: {
			quickInsert:
				!readonly &&
				!isMacroDeprecated(extensionKey) &&
				editorActions &&
				shouldIncludeMacroInQuickInsert(extensionKey)
					? createQuickInsertModule(
							macro,
							pageContext,
							openMacroBrowserForInitialConfiguration,
							editorAPI,
							intl,
							macroBrowserConfig,
							editorActions,
							createAnalyticsEvent,
							onCompleteCallback,
							isLivePage,
						)
					: [],
			nodes: {
				default: defaultNode,
			},
			fields: {
				custom: {
					attachment: {
						resolver: getAttachmentResolverFor(macro, pageContext),
					},
					spacekey: {
						resolver: spacekeyResolver(macro),
					},
					username: {
						resolver: usernameResolver,
					},
					label: {
						resolver: labelResolver,
					},
					labelWithPlusMinusPrefixes: {
						resolver: customPrefixResolver({
							resolver: labelResolver,
							prefixSettings: PLUS_MINUS_PREFIX_SETTINGS(intl),
						}),
					},
					'confluence-content-by-id': {
						resolver: confluenceContentResolver,
					},
					'confluence-content': {
						resolver: unsupportedValueResolver(getSpaceKeyAndPageTitleContentResolver()),
					},
					spaceAndPage: {
						resolver: spaceAndPageResolver,
					},
					spaceKeyAndPageTitleContent: {
						resolver: getSpaceKeyAndPageTitleContentResolver(),
					},
					rootPage: {
						resolver: customValueResolver({
							resolver: getSpaceKeyAndPageTitleContentResolver(),
							values: ['@self', '@home', '@parent', '@none'],
						}),
					},
					calendar: {
						resolver: calendarResolver,
					},
					template: {
						resolver: getTemplateResolver(pageContext),
					},
					blogRecentlyUpdatedSpace: {
						resolver: customValueResolver({
							/* recently updated macro doesn't support currentSpace() */
							resolver: buildSpacekeyResolver(false),
							values: ['@self', '@personal', '@global', '@favorite', '@favourite', '@all', '*'],
						}),
					},
					blogRecentlyUpdatedSpaceWithPlusMinusPrefixes: {
						resolver: customValueResolver({
							/* blog posts macro doesn't support currentSpace() */
							resolver: customPrefixResolver({
								/* blog posts macro needs support for requiring or excluding spaces with "+" or "-" prefixes.*/
								resolver: buildSpacekeyResolver(false),
								prefixSettings: PLUS_MINUS_PREFIX_SETTINGS(intl),
							}),
							values: ['@self', '@personal', '@global', '@favorite', '@favourite', '@all', '*'],
						}),
					},
					contributorsSpaceParamResolver: {
						resolver: customValueResolver({
							resolver: buildSpacekeyResolver(false),
							values: ['@personal', '@global', '@all'],
						}),
					},
					recentlyUpdatedDashboardSpaceKeyResolver: {
						resolver: customValueResolver({
							resolver: buildSpacekeyResolver(false),
							values: ['*'],
						}),
					},
					widthPercentage: {
						resolver: widthPercentageResolver(intl),
					},
				},
				fieldset: {
					cql: {
						serializer: cqlSerializer,
						deserializer: cqlDeserializer,
					},
				},
			},
		},
	};
};

export const getConfluenceMacrosExtensionProviderForEditor = async (
	pageContext: ConfluencePageContext,
	extensionHandlers: ExtensionHandlers,
	editorAPI: PublicPluginAPI<[ExtensionPlugin]> | undefined,
	editorActions: EditorActions,
	macroBrowserConfig: MacroConfig,
	getMigrationExtensionManifest: Function,
	openMacroBrowserToEditExistingMacro: Function,
	openMacroBrowserForInitialConfiguration: Function,
	intl: IntlShape,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	onCompleteCallback?: OnCompleteCallbackFnType,
	isLivePage?: boolean,
	flags?: FlagsStateContainer,
	experienceTracker?: ExperienceTrackerAPI,
	contentType?: string,
	allowCreateDatabaseExtension: boolean = false,
	allowDatabaseQuickInsert: boolean = false,
	allowCreateWhiteboardExtension: boolean = false,
	allowCreateWithRovoExtension: boolean = false,
	openSmartCreationModal?: (params: OpenModalParams) => void,
	allowLegacyMacros: boolean = false,
	isCompanyHub?: boolean,
	pageTreeStateUpdater?: PageTreeStateUpdater,
): Promise<ExtensionProvider> => {
	const [legacyManifests, macroAutoConverters] = await Promise.all([
		getMacroLegacyManifest(pageContext),
		getAutoConverter(macroBrowserConfig),
	]);

	// Block unsupported 1p macro insertion if we're in the Company Hub,
	// the company-hub-migration-prevent-1p-macro experiment is enabled,
	// and the company-hub-prevent-1p-macro-via-tap-trait gate returns false.
	// company-hub-prevent-1p-macro-via-tap-trait is controlled by a tap trait that checks if a tenant's company hub
	// has been published with unsupported 1p macros in the last 4 months,
	// in which case we will allow them to insert unsupported 1p macros.
	const hideMacrosOptions = {
		isLivePage,
		isCompanyHub:
			isCompanyHub &&
			expVal('company-hub-migration-prevent-1p-macro', 'isEnabled', false) &&
			!fg('company-hub-prevent-1p-macro-via-tap-trait'),
	};
	const legacyManifestMacros = filterOutHiddenMacros(legacyManifests.macros, hideMacrosOptions);

	let modernManifests = await mapLegacyManifestsToModernManifest(
		legacyManifestMacros,
		pageContext,
		extensionHandlers,
		getMigrationExtensionManifest,
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		editorAPI,
		intl,
		macroBrowserConfig,
		editorActions,
		createAnalyticsEvent,
		onCompleteCallback,
		isLivePage,
		flags,
		allowLegacyMacros,
	);

	if (fg('cq_native_macro')) {
		const nativeManifest = modernManifests.find((m) => m.key === 'questionslist-macro-native');
		const connectManifest = modernManifests.find((m) => m.key === 'questionslist-macro');

		if (nativeManifest) {
			if (connectManifest) {
				modernManifests = modernManifests.filter((m) => m.key !== 'questionslist-macro');
			}

			// Keep the native manifest (for new macros)
			// Add hijacked manifest (for existing Connect macros)
			// This creates TWO manifests:
			// 1. 'questionslist-macro-native' -> for new macros created via /questions
			// 2. 'questionslist-macro' -> for existing Connect macros in documents
			const hijackedManifest = {
				...nativeManifest,
				key: 'questionslist-macro', // Use Connect key for existing macro lookups
			};

			modernManifests.push(hijackedManifest);
		}
	} else {
		const nativeManifest = modernManifests.find((m) => m.key === 'questionslist-macro-native');
		if (nativeManifest) {
			modernManifests = modernManifests.filter((m) => m.key !== 'questionslist-macro-native');
		}
	}

	modernManifests = modernManifests.filter((manifest) => manifest.key !== 'toc');

	const macro = legacyManifestMacros.find((m) => m.macroName === 'toc');
	const { tableOfContentsExtension } = await import(
		/* webpackChunkName: "loadable-TableOfContents" */ './custom-extensions/TableOfContents.tsx'
	);

	const createTableOfContentsExtension = tableOfContentsExtension({
		macro,
		pageContext,
		extensionHandlers,
		editorAPI,
		openMacroBrowserForInitialConfiguration,
		macroBrowserConfig,
		editorActions,
		createAnalyticsEvent,
		intl,
		onCompleteCallback,
		isLivePage,
	});

	modernManifests.push(createTableOfContentsExtension);

	if (fg('confluence_dlp_content_redaction')) {
		modernManifests.push(createTourableRedactionExtension({ contentId: pageContext?.contentId }));
	}

	modernManifests.push(nestedTableExtension);

	if (allowCreateDatabaseExtension && isFeatureEnabled('database')) {
		modernManifests.push(
			createDatabaseExtension({
				pageId: pageContext.contentId,
				spaceKey: pageContext.spaceKey!,
				spaceId: pageContext.spaceId!,
				intl,
				flags,
				createAnalyticsEvent,
				experienceTracker,
				contentType,
				allowDatabaseQuickInsert:
					allowDatabaseQuickInsert && !fg('confluence_frontend_databases_opt-out'),
				isEdit: true,
				pageTreeStateUpdater,
			}),
		);
	}

	if (
		allowCreateWhiteboardExtension &&
		pageContext.spaceId &&
		pageContext.spaceKey &&
		isFeatureEnabled('whiteboard') &&
		expVal('create_whiteboard_via_slash_command', 'enabled', false)
	) {
		modernManifests.push(
			createWhiteboardExtension({
				pageId: pageContext.contentId,
				spaceKey: pageContext.spaceKey,
				spaceId: pageContext.spaceId,
				intl,
				flags,
				experienceTracker,
				createAnalyticsEvent,
				contentType,
				allowWhiteboardQuickInsert: allowCreateWhiteboardExtension,
				pageTreeStateUpdater,
			}),
		);
	}

	if (
		allowCreateWithRovoExtension &&
		openSmartCreationModal &&
		['slot-two', 'slot-four'].includes(expVal('cc_fd_cwr_quick_insert', 'cohort', 'control'))
	) {
		modernManifests.push(createCreateWithRovoExtension(intl, pageContext, openSmartCreationModal));
	}

	return new DefaultExtensionProvider(modernManifests, [
		(link: string) => {
			const result = macroAutoConverters(link);

			if (result) {
				return result;
			}
		},
	]);
};

export const getConfluenceMacrosExtensionProviderForRenderer = async (
	pageContext: ConfluencePageContext,
	extensionHandlers: RendererExtensionHandlers,
	getMigrationExtensionManifest: Function,
	openMacroBrowserToEditExistingMacro: Function,
	openMacroBrowserForInitialConfiguration: Function,
	intl: IntlShape,
	createAnalyticsEvent?: CreateUIAnalyticsEvent,
	providerFactory?: ProviderFactory,
): Promise<ExtensionProvider> => {
	const chartsExtensionPromise = createChartsExtension({
		// TODO: use intl5 instead
		intl: intl as any,
		extensionHandlers: extensionHandlers[
			EXTENSION_TYPE.MACRO_WITH_REFERENCES
		] as ExtensionHandlerWithReferenceFn<any>,
		createAnalyticsEvent,
	});

	const legacyManifests = await getMacroLegacyManifest(pageContext);
	let modernManifests: ExtensionManifest<any>[] = await mapLegacyManifestsToModernManifest(
		legacyManifests.macros,
		pageContext,
		extensionHandlers,
		getMigrationExtensionManifest,
		openMacroBrowserToEditExistingMacro,
		openMacroBrowserForInitialConfiguration,
		// We don't have the `editorAPI` for the renderer
		undefined,
		intl,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
		undefined,
	);

	modernManifests.push(getRedactedMacroPlaceholderExtensionManifest(extensionHandlers));

	if (chartsExtensionPromise) {
		modernManifests.push(await chartsExtensionPromise);
	}

	// We don't want to check for the aiOptInStatus as we want to show the rendered block
	// regardless of AI status so we don't lose content if people downgrade etc
	const aiBlockProvider = await getAIPanelsProvider({
		intl: intl as any,
		providerFactory,
	});
	modernManifests.push(...(await aiBlockProvider.getExtensions()));

	const { contentId } = pageContext;

	modernManifests.push(
		...(await createPremiumExtensionManifests({
			contentId,
			// @ts-expect-error — react-intl v5/v7 type mismatch, will be removed when platform upgrades to react-intl v7
			intl,
			allowCompanyHubExclusiveExtensions: true,
		})),
	);

	if (isFeatureEnabled('database')) {
		modernManifests.push(
			createDatabaseExtension({
				pageId: contentId,
				spaceKey: pageContext.spaceKey!,
				spaceId: pageContext.spaceId!,
				intl,
				createAnalyticsEvent,
				isEdit: false,
			}),
		);
	}

	if (fg('confluence_dlp_content_redaction')) {
		modernManifests.push(createTourableRedactionExtension({ contentId }));
	}

	modernManifests.push(nestedTableExtension);

	if (expVal('cc-maui-experiment', 'isEnabled', false)) {
		modernManifests.push(createNativeEmbedManifest());
	}

	return new DefaultExtensionProvider(modernManifests);
};
